import Haikunator from "haikunator";
import { equals, find, map, not, pipe, propEq, split } from "ramda";
import uuid from "uuid/v4";

import { getPrettyUrl } from "get_base_uri";
import { transformCartItem } from "packages/online_ordering/api/helpers/cart_item";
import type { CartState } from "packages/online_ordering/reducers/cart";
import type { CheckoutState } from "packages/online_ordering/reducers/checkout";
import type { PaymentState } from "packages/online_ordering/reducers/checkout/payment";
import type { ConsumerState } from "packages/online_ordering/reducers/consumer/consumer";
import type { IOrderPayload } from "packages/online_ordering/types/IOrderPayload";
import type { IOrderingOptionsState } from "types/reducers/ordering_options";
import type { OrderPaymentPayload } from "types/reducers/payment";
import type { Fulfillment, StoreInfo } from "types/reducers/store_info";

export function formatPaymentType(paymentType) {
  switch (paymentType) {
    case "credit":
      return "CREDIT";
    case "gift_card":
      return "GIFT_CARD";
    default:
      return "CASH";
  }
}

export function formatFulfillmentType(selectedTransport) {
  switch (selectedTransport) {
    case "Delivery":
      return "delivery";
    case "Dine in":
      return "dine_in";
    default:
      return "pickup";
  }
}

export function parseOrderAheadTime(time) {
  const timeParts = split(" ", time);

  const [hourAndMinute, amPM] = timeParts;
  const [hours, minutes] = pipe(
    split(":"),
    map(str => parseInt(str, 10))
  )(hourAndMinute);

  let adjustedHours = hours;
  if (amPM.startsWith("PM") && not(equals(12, hours))) {
    adjustedHours = hours + 12;
  } else if (amPM.startsWith("AM") && equals(12, hours)) {
    adjustedHours = hours - 12;
  }

  return { hours: adjustedHours, minutes };
}

export function generatePaymentsPayload(
  payment: PaymentState
): OrderPaymentPayload[] {
  const {
    giftCards,
    calculatedTotals: { payments },
  } = payment;
  return payments.payments
    .map((orderPayment): OrderPaymentPayload | undefined => {
      const paymentTipAmount = orderPayment.tipAmount
        ? orderPayment.tipAmount
        : 0;
      const paymentAmount = orderPayment.amount + paymentTipAmount; // amount here is the amount charged to the payment method so this includes the tip

      if (orderPayment.paymentType === "CREDIT") {
        if (orderPayment.amount === 0 && orderPayment.tipAmount === 0) {
          // When $0 is paid via Credit, we don't actually charge the card.
          // Subsequently, we don't send it to Ubergateway and shouldn't tell the Point-of-Sale
          // that there's an associated payment for it.
          // The only way this case should be hit would be if someone:
          // * paid with a gift card, the store doesn't accept cash, the gift card covered the total, and they didn't tip
          // * paid with a credit card, applied a promo code so the amount due was zero, and they didn't tip
          // We can probably simplify all this by filtering out these conditions when we initially populate
          // the payments object in checkout.

          // We replace it with a cash payment if it is the only option selected
          if (payments.payments.length === 1) {
            return {
              paymentType: "CASH",
              amount: 0,
              tipAmount: 0,
            };
          } else {
            return undefined;
          }
        }

        return {
          paymentType: orderPayment.paymentType,
          amount: paymentAmount,
          tipAmount: paymentTipAmount,
        };
      }

      if (orderPayment.paymentType === "GIFT_CARD") {
        const pin = giftCards.find(gc => gc.index === orderPayment.index)?.pin;
        if (!pin) {
          throw new Error("PIN not found");
        }
        return {
          amount: paymentAmount,
          giftCardNumber: orderPayment.giftCardNumber,
          paymentType: orderPayment.paymentType,
          pin,
          tipAmount: paymentTipAmount,
        };
      }

      // CASH
      return {
        paymentType: orderPayment.paymentType,
        amount: paymentAmount,
        tipAmount: paymentTipAmount,
      };
    })
    .filter(
      (paymentMethod): paymentMethod is NonNullable<typeof paymentMethod> =>
        paymentMethod !== undefined
    );
}

export function orderTransform({
  storeInfo,
  checkout,
  consumerInfo,
  cart,
  orderingOptions,
  sessionId,
}: {
  storeInfo: StoreInfo;
  checkout: CheckoutState;
  consumerInfo: ConsumerState;
  cart: CartState;
  orderingOptions: IOrderingOptionsState;
  sessionId: string;
}): IOrderPayload {
  const {
    payment: { calculatedTotals, promoCode, promoCodeIsValid },
  } = checkout;
  const { contactInfo } = consumerInfo;
  const { permalink } = storeInfo;
  const orderId = uuid();
  const haikunator = new Haikunator({
    adjectives: [permalink],
    nouns: [],
    defaults: {
      delimiter: "-",
      tokenLength: 5,
    },
  });

  const prettyUrl = getPrettyUrl();

  const {
    deliveryFee: feeByMethod,
    tax,
    orderTotal,
    fees,
    withFeeIncluded,
    total,
  } = calculatedTotals;

  let addressForOrder: Pick<IOrderPayload, "line1" | "city" | "state" | "zip"> =
    {};
  if (equals(orderingOptions.selectedTransport, "Delivery")) {
    addressForOrder = {
      line1: consumerInfo.contactInfo.address.line1,
      city: consumerInfo.contactInfo.address.city,
      state: consumerInfo.contactInfo.address.state,
      zip: consumerInfo.contactInfo.address.zip,
    };
  }

  let orderAhead: Record<string, unknown> = {};
  if (equals(orderingOptions.selectedTimeFrame, "Later")) {
    const { orderAheadDate, orderAheadTime } = orderingOptions;
    const parsedTime = parseOrderAheadTime(orderAheadTime);

    if (typeof orderAheadDate === "string") {
      throw new Error("orderAheadDate must be an object");
    }

    const promisedDate = orderAheadDate.dayAsMoment
      .hour(parsedTime.hours)
      .minute(parsedTime.minutes)
      .second(0)
      .millisecond(0)
      .toDate();

    orderAhead = {
      promisedDate,
    };
  }

  let dineInFulfillmentInfo: Pick<IOrderPayload, "dineInInfo"> = {};
  if (equals(orderingOptions.selectedTransport, "Dine in")) {
    const dineInOption = find(
      propEq("name", orderingOptions.selectedTransport),
      storeInfo.fulfillmentTypes
    ) as Fulfillment;
    let dineInCheckType: "tab" | "table" = "tab";
    if (dineInOption && dineInOption.checkTypes) {
      [dineInCheckType] = dineInOption.checkTypes;
    }
    dineInFulfillmentInfo = {
      dineInInfo: {
        checkType: dineInCheckType,
        tableId: orderingOptions.dineInTableId,
      },
    };
  }

  let pickupFulfillmentInfo: Record<string, unknown> = {};
  if (
    equals(orderingOptions.selectedTransport, "Pick up") &&
    orderingOptions.isCurbsidePickup
  ) {
    pickupFulfillmentInfo = {
      pickupInfo: {
        isCurbsidePickup: orderingOptions.isCurbsidePickup,
        curbsidePickupInstructions: orderingOptions.curbsidePickupInstructions,
      },
    };
  }

  let deliveryFulfillmentInfo: Record<string, unknown> = {};
  if (equals(orderingOptions.selectedTransport, "Delivery")) {
    deliveryFulfillmentInfo = {
      deliveryInfo: {
        address: {
          addressLine1: contactInfo.address.line1,
          addressLine2: contactInfo.address.line2,
          city: contactInfo.address.city,
          state: contactInfo.address.state,
          country: "",
          zipCode: contactInfo.address.zip,
        },
      },
    };
  }

  return {
    order: {
      id: orderId,
      timePlaced: new Date().toISOString(),
      confirmationCode: haikunator.haikunate(),
      fulfillmentInfo: {
        type: formatFulfillmentType(orderingOptions.selectedTransport),
        instructions: orderingOptions.deliveryInstructions,
        customer: {
          firstName: contactInfo.firstName,
          lastName: contactInfo.lastName,
          phone: contactInfo.phone,
          email: contactInfo.email,
        },
        ...pickupFulfillmentInfo,
        ...deliveryFulfillmentInfo,
        ...dineInFulfillmentInfo,
      },
      payments: {
        total: withFeeIncluded ? total : orderTotal,
        payments: generatePaymentsPayload(checkout.payment),
      },
      charges: {
        fees: feeByMethod,
        items: map(transformCartItem, cart.cartItems),
        taxes: tax,
        tip: {
          amount: calculatedTotals.payments.payments.reduce(
            (tipTotal, payment) => tipTotal + payment.tipAmount,
            0
          ),
        },
        total: withFeeIncluded ? total : orderTotal,
        deliveryFee: fees.deliveryFee,
        consumerFee: fees.consumerFee,
        serviceFee: fees.serviceFee,
        feeTotal: fees.total,
        feeVersion: fees.version,
      },
      promoCode: promoCodeIsValid ? promoCode : "",
      ...orderAhead,
    },
    orderTotal, // Do not send Upserve Fee as part of final total. It will be determined by the backend.
    email: consumerInfo.contactInfo.email,
    firstName: consumerInfo.contactInfo.firstName,
    lastName: consumerInfo.contactInfo.lastName,
    ...addressForOrder,
    phoneNumber: consumerInfo.contactInfo.phone,
    textAlerts: consumerInfo.contactInfo.textAlerts,
    storePrettyUrl: prettyUrl,
    submissionId: orderId,
    sessionId,
  };
}
